import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { COLLAPSE } from "../../lib/motion";

interface DangerActionProps {
  /** Libellé au repos, avant que la question ne soit posée. */
  label: string;
  /** La question, courte : elle partage la ligne avec les deux réponses. */
  question: string;
  /** Libellé de la confirmation elle-même. */
  confirmLabel: string;
  icon: React.ReactNode;
  pending?: boolean;
  disabled?: boolean;
  onConfirm: () => void;
}

/**
 * Une action sans retour se demande deux fois. Le motif est le même partout —
 * salon, partie, retrait d'un ami — et la confirmation remplace le bouton sur
 * place plutôt que d'ouvrir une boîte : sur mobile, une boîte de plus par-dessus
 * un plateau déjà chargé se ferme au premier geste de travers.
 */
export const DangerAction = ({
  label,
  question,
  confirmLabel,
  icon,
  pending = false,
  disabled = false,
  onConfirm,
}: DangerActionProps) => {
  const [asking, setAsking] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (asking) confirmRef.current?.focus();
  }, [asking]);

  return (
    <div className="danger-action">
      <AnimatePresence initial={false} mode="wait">
        {asking ? (
          <motion.div
            className="danger-action__confirm"
            key="confirm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={COLLAPSE}
          >
            <span className="danger-action__question">{question}</span>
            <button
              ref={confirmRef}
              className="secondary-button secondary-button--danger"
              type="button"
              disabled={pending}
              onClick={onConfirm}
            >
              {pending ? <span className="button-spinner" aria-hidden="true" /> : icon}
              {confirmLabel}
            </button>
            <button
              className="ghost-button"
              type="button"
              disabled={pending}
              onClick={() => setAsking(false)}
            >
              Annuler
            </button>
          </motion.div>
        ) : (
          <motion.button
            className="secondary-button secondary-button--danger"
            key="idle"
            type="button"
            disabled={disabled}
            onClick={() => setAsking(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={COLLAPSE}
          >
            {icon}
            {label}
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};
